import { ErrorType } from "@/composable/application/errors";
import { RATE_LIMITED_ERROR_FLAG } from "@/composable/http/rateLimitHandler";
import i18n from "@/i18n";
import { GenericDialogParams } from "@/types/front/GenericDialogParams";
import { BuefyTypes } from "@/utils/BuefyUtils";
import { ToastProgrammatic as Toast } from "buefy";

/**
 * Vrai si l'erreur a déjà été signalée à l'utilisateur par le
 * {@code Fetcher} via le notifier rate-limit ( cas 429 ) . Permet
 * d'éviter un second toast générique "erreur serveur" pour la même
 * cause , ce qui désorienterait l'utilisateur .
 */
function isAlreadyHandledRateLimit(error?: ErrorType | null): boolean {
  if (!error) return false;
  return (error as unknown as Record<string, unknown>)[RATE_LIMITED_ERROR_FLAG] === true;
}

const TOAST_INFO_DURATION = 3000;
const TOAST_ERROR_DURATION = 5000;
const TOAST_POSITION = "is-top";

export class AlertService {
  static INSTANCE: AlertService = new AlertService();

  /**
   * Affiche un toast de succes . {@code duration} optionnelle pour les
   * messages necessitant une lecture plus longue ( ex : confirmation
   * d'envoi de mail avec adresse a verifier ) . Defaut : 3s .
   */
  toastSuccess(message: string, duration?: number): void {
    new Toast().open({
      message: message,
      type: BuefyTypes.SUCCESS,
      duration: duration ?? TOAST_INFO_DURATION,
      position: TOAST_POSITION,
    });
  }

  toastWarn(message: string, error?: ErrorType, time?: number): void {
    console.warn("[WARNING] " + message, error);
    new Toast().open({
      message: message,
      type: BuefyTypes.WARNING,
      duration: time || TOAST_ERROR_DURATION,
      position: TOAST_POSITION,
    });
  }

  /**
   * Affiche un toast rouge avec le message tel quel . Contrairement a
   * {@link toastError} , ne lit PAS {@code error.content} pour resoudre
   * un code backend via {@code exceptionMessage.<CODE>} . A utiliser
   * quand on veut imposer un message frontend explicite sans dependre
   * du body de la reponse HTTP ( ex : "Invalid Email" sur flow
   * forgot-password ou le backend retourne {@code BAD_LOGIN_OR_EMAIL_PASSWORD}
   * mais le frontend veut afficher un libelle plus pertinent au contexte ) .
   */
  toastErrorMessage(message: string, time?: number): void {
    new Toast().open({
      message: message,
      type: BuefyTypes.DANGER,
      duration: time || TOAST_ERROR_DURATION,
      position: TOAST_POSITION,
    });
  }

  toastError(message: string, error?: ErrorType, state?: { value: unknown }, time?: number): void {
    if (isAlreadyHandledRateLimit(error)) {
      if (state) state.value = error;
      return;
    }
    if (error?.content) {
      // {@code error.content} peut etre une Promise ( reponse streaming
      // pas encore lue ) ou l'objet JSON deja resolu ( cas du chemin
      // d'erreur 4xx ou {@code Fetcher} fait {@code await response.json()}
      // avant de rejecter ) . {@code Promise.resolve} normalise les deux .
      Promise.resolve(error.content).then((t: unknown) => {
        if (typeof t === "string") {
          const regExpMatchArray = t.match("application inconnue (.*)");
          if (regExpMatchArray) {
            new Toast().open({
              message: i18n.global.t("applications.no-right-for-application"),
              type: BuefyTypes.DANGER,
              duration: time || TOAST_ERROR_DURATION,
              position: TOAST_POSITION,
            });
            if (state) state.value = "NO-RIGHTS";
          }
          else {
            new Toast().open({
              message: i18n.global.t("exceptionMessage." + t),
              type: BuefyTypes.DANGER,
              duration: TOAST_ERROR_DURATION,
              position: TOAST_POSITION,
            });
          }
        }
        else if (typeof t === "object" && t !== null && "message" in t
          && (t as Record<string, unknown>).message === "NO_RIGHT_FOR_APPLICATION_MANAGEMENT") {
          if (state) state.value = "NO-RIGHTS";
        }
        else if (typeof t === "object" && t !== null && "message" in t) {
          const record = t as Record<string, unknown>;
          const parseMessage = /(^[^[]*) \[(.*)\]$/.exec(String(record.message));
          const msgKey = parseMessage?.[1] || String(record.message);
          const params: Record<string, string> = typeof record.params === "object" && record.params !== null
            ? { ...record.params as Record<string, string> }
            : {};
          (parseMessage?.[2].split(",") || []).forEach(
            (param: string, index: number) => (params["param" + index] = param.trim()),
          );
          new Toast().open({
            message: i18n.global.t("exceptionMessage." + msgKey, params),
            type: BuefyTypes.DANGER,
            duration: TOAST_ERROR_DURATION,
            position: TOAST_POSITION,
          });
          if (state) state.value = t;
        }
        else {
          if (state) state.value = t;
        }
      });
    }
    else {
      new Toast().open({
        message: message,
        type: BuefyTypes.DANGER,
        duration: TOAST_ERROR_DURATION,
        position: TOAST_POSITION,
      });
    }
  }

  toastServerError(error?: ErrorType, state?: { value: unknown }): void {
    console.error("Alert Services - server error", error);
    if (isAlreadyHandledRateLimit(error)) {
      if (state) state.value = error;
      return;
    }
    if (error?.content != null) {
      // {@code error.content} peut etre soit une Promise ( reponse en
      // streaming pas encore lue ) , soit l'objet JSON deja resolu
      // ( cas du chemin d'erreur 4xx ) . {@code Promise.resolve} unifie
      // les deux sans crash {@code .then is not a function} .
      Promise.resolve(error.content).then((value: unknown) => {
        const message = typeof value === "object" && value !== null && "message" in value
          ? String((value as Record<string, unknown>).message)
          : String(value);
        this.toastError(message, error, state);
      });
    }
    else {
      this.toastError(i18n.global.t("alert.server-error"), error, state);
    }
  }

  confirm(
    title: string,
    message: string,
    confirmText: string,
    type: string = "warning",
    cancelMsg: string,
    onConfirmCb: () => void = () => {},
  ): void {
    const dialogParams = new GenericDialogParams();
    dialogParams.title = title;
    dialogParams.message = message;
    dialogParams.confirmText = confirmText;
    dialogParams.type = type;
    dialogParams.cancelText = cancelMsg;
    dialogParams.hasIcon = true;
    dialogParams.onConfirm = onConfirmCb;
    document.dispatchEvent(new CustomEvent("show-dialog", { detail: dialogParams }));
  }

  info(
    title: string,
    message: string,
    btnConfirm: string,
    cancelMsg: string,
    autoCloseAfter?: number, // optionnel
  ): void {
    const dialogParams = new GenericDialogParams();
    dialogParams.title = title;
    dialogParams.message = message;
    dialogParams.confirmText = btnConfirm;
    dialogParams.cancelText = cancelMsg;
    dialogParams.autoCloseAfter = autoCloseAfter;
    document.dispatchEvent(new CustomEvent("show-dialog", { detail: dialogParams }));
  }

  prompt(dialogParams: GenericDialogParams): void {
    document.dispatchEvent(
      new CustomEvent("show-dialog", new CustomEvent("show-dialog", { detail: dialogParams })),
    );
  }
}
